import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { IconPlayerPlayFilled } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { formatClock, formatShortDate } from "../../lib/format-date"
import { displayMeetingTitle } from "../../lib/meeting-title"
import { ChannelLabel, channelMeta } from "../channel-meta"
import {
  buildHighlightRanges,
  formatUnresolvedSpeakerLabel,
  prepareSnippetContent,
  toSegments,
  windowSnippet,
} from "./helpers"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * A single search result. Pure presentation: the whole card is a router `<Link>`
 * to the source meeting, deep-linking to the exact moment (`?t=`) when the hit
 * carries a seek position (transcript / quote turns). The channel drives a
 * semantic `data-tone` accent + icon (see `channel-meta`) — never a literal
 * palette name, per theming rule #6.
 *
 * When a `query` is supplied its tokens are highlighted inside the snippet and
 * the snippet is windowed around the first match so the matched context stays
 * within the 3-line clamp (see `helpers.ts`).
 */
export const SearchHitCard = ({ hit, query = "" }: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const { tone, Icon } = channelMeta(hit.sourceChannel)
  const seekStart = hit.seekStart
  const canSeek = seekStart != null
  const date = formatShortDate(i18n, hit.meetingDate)
  const title = displayMeetingTitle(hit.meetingSubject, null, i18n)

  const speakerLabel = hit.speaker ? formatUnresolvedSpeakerLabel(hit.speaker) : null
  const content = prepareSnippetContent(hit.content, hit.speaker)
  const ranges = query.length > 0 ? buildHighlightRanges(content, query) : []
  const snippet = windowSnippet(content, ranges)
  const segments = toSegments(snippet.text, snippet.ranges)

  return (
    <Link
      to="/meetings/$id"
      params={{ id: hit.meetingId }}
      search={canSeek ? { t: Math.floor(seekStart) } : {}}
      className={`${classes.card} lex-surface-card`}
      data-tone={tone}
      data-testid="search-hit"
    >
      <div className={classes.head}>
        <span className={classes.channel}>
          <Icon size={14} />
          <ChannelLabel sourceChannel={hit.sourceChannel} />
        </span>
        <span className={classes.meeting}>
          <span className={classes.subject}>{title}</span>
          <span className={`${classes.dot} lex-dot`} aria-hidden />
          <time className={classes.date}>{date}</time>
        </span>
      </div>

      {speakerLabel ? <div className={classes.speaker}>{speakerLabel}</div> : null}

      <p className={classes.content}>
        {snippet.leadingEllipsis && <span aria-hidden>{"… "}</span>}
        {segments.map((segment, index) =>
          segment.highlight ? (
            <mark key={index} className={classes.mark}>
              {segment.text}
            </mark>
          ) : (
            <span key={index}>{segment.text}</span>
          ),
        )}
      </p>

      {canSeek && (
        <span className={classes.seek}>
          <IconPlayerPlayFilled size={11} />
          <Trans>Play from</Trans> {formatClock(seekStart)}
        </span>
      )}
    </Link>
  )
}
